import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";

const CONTENT = {
  en: {
    title: "Privacy Policy",
    lastUpdated: "Last updated: 10 August 2026",
    intro:
      "Wall Board Academy (\"we\", \"our\", \"us\") respects your privacy. This document explains what personal data we collect when you use www.wallboardacademy.com and the Wall Board Academy mobile app, how we use it, and the rights you have under the General Data Protection Regulation (EU 2016/679, \"GDPR\").",
    sections: [
      {
        h: "1. Data Controller",
        p: "Wall Board Academy — Federico Fiori MD, Head of Abdominal Wall Surgery Unit, ASST Santi Paolo e Carlo — Milano, Italy. Contact: info@wallboardacademy.com.",
      },
      {
        h: "2. Data we collect",
        list: [
          "Registration data: email, first name, last name, date of birth, institution, specialization.",
          "Authentication data: encrypted password, session token (JWT).",
          "Usage data: pages visited, videos watched, watch-time (in seconds), live session presence (only aggregated counts).",
          "Technical data: IP address, browser and device type, referrer, timestamps.",
          "Cookies and analytics: PostHog session analytics (anonymised) for product improvement.",
        ],
      },
      {
        h: "3. How we use your data",
        list: [
          "To create and manage your account and grant access to the content library.",
          "To send transactional email (approval, password reset, upcoming live reminders) via Resend (EU processor).",
          "To broadcast live sessions via YouTube Data API (Google LLC) — only when you actively join a live event.",
          "To analyse content performance in an aggregated form (analytics dashboard for admins).",
          "To comply with legal obligations.",
        ],
      },
      {
        h: "4. Legal basis (GDPR art. 6)",
        list: [
          "Contract (art. 6.1.b) — for account creation and delivery of the service.",
          "Legitimate interest (art. 6.1.f) — for security, analytics, and product improvement.",
          "Consent (art. 6.1.a) — for optional marketing communications and cookies (you can withdraw anytime).",
        ],
      },
      {
        h: "5. Third parties",
        list: [
          "Resend (email delivery) — https://resend.com/legal/privacy-policy",
          "Google LLC (YouTube Data API for live streaming) — https://policies.google.com/privacy",
          "PostHog (product analytics) — https://posthog.com/privacy",
          "MongoDB Atlas (database hosting) — https://www.mongodb.com/legal/privacy-policy",
          "Aruba S.p.A. (hosting provider) — https://www.aruba.it/informativa-privacy.aspx",
        ],
      },
      {
        h: "6. Data retention",
        list: [
          "Account data: retained until you request deletion.",
          "View and heartbeat logs: automatically deleted after 36 months.",
          "Live-session Q&A messages: automatically deleted after 36 months.",
          "Backups: encrypted, rotated every 30 days.",
        ],
      },
      {
        h: "7. Your rights",
        p: "Under GDPR you have the right to access, rectify, erase, restrict, port or object to the processing of your data. You may also lodge a complaint with the Italian Data Protection Authority (Garante per la Protezione dei Dati Personali). To exercise any right, email info@wallboardacademy.com — we reply within 30 days.",
      },
      {
        h: "8. Cookies",
        p: "We use only strictly necessary cookies (session, authentication) and one analytics cookie set by PostHog. No advertising or third-party tracking cookies are used.",
      },
      {
        h: "9. Children",
        p: "The platform is intended for medical professionals aged 18 and above. We do not knowingly collect data from minors.",
      },
      {
        h: "10. Security",
        p: "Passwords are hashed with bcrypt. All traffic is encrypted via HTTPS/TLS 1.3. Access to the database is restricted by IP allow-list and requires MFA.",
      },
      {
        h: "11. Changes to this policy",
        p: "This Privacy Policy may be updated. The date of the last revision is shown at the top. Significant changes will be communicated by email.",
      },
      {
        h: "12. Contact",
        p: "Wall Board Academy — info@wallboardacademy.com — www.wallboardacademy.com",
      },
    ],
    back: "← Back to home",
  },
  it: {
    title: "Informativa sulla Privacy",
    lastUpdated: "Ultimo aggiornamento: 10 agosto 2026",
    intro:
      "Wall Board Academy (\"noi\", \"la nostra\", \"noi\") rispetta la tua privacy. Il presente documento descrive quali dati personali raccogliamo quando utilizzi www.wallboardacademy.com e l'app mobile Wall Board Academy, come li utilizziamo e quali diritti hai ai sensi del Regolamento Generale sulla Protezione dei Dati (UE 2016/679, \"GDPR\").",
    sections: [
      {
        h: "1. Titolare del trattamento",
        p: "Wall Board Academy — Federico Fiori MD, Responsabile Unità di Chirurgia della Parete Addominale, ASST Santi Paolo e Carlo — Milano, Italia. Contatto: info@wallboardacademy.com.",
      },
      {
        h: "2. Dati che raccogliamo",
        list: [
          "Dati di registrazione: email, nome, cognome, data di nascita, istituzione, specializzazione.",
          "Dati di autenticazione: password cifrata, token di sessione (JWT).",
          "Dati di utilizzo: pagine visitate, video guardati, tempo di visione (in secondi), presenza nelle dirette (solo conteggi aggregati).",
          "Dati tecnici: indirizzo IP, tipo di browser e dispositivo, referrer, timestamp.",
          "Cookie e analytics: analisi di sessione PostHog (anonimizzate) per il miglioramento del prodotto.",
        ],
      },
      {
        h: "3. Come usiamo i tuoi dati",
        list: [
          "Per creare e gestire il tuo account e concederti l'accesso alla libreria di contenuti.",
          "Per inviare email transazionali (approvazione, reset password, promemoria dirette) tramite Resend (processore UE).",
          "Per trasmettere le sessioni live tramite YouTube Data API (Google LLC) — solo quando partecipi attivamente ad un evento live.",
          "Per analizzare le performance dei contenuti in forma aggregata (dashboard admin).",
          "Per adempiere ad obblighi di legge.",
        ],
      },
      {
        h: "4. Base giuridica (GDPR art. 6)",
        list: [
          "Contratto (art. 6.1.b) — per la creazione dell'account e l'erogazione del servizio.",
          "Legittimo interesse (art. 6.1.f) — per sicurezza, analytics e miglioramento del prodotto.",
          "Consenso (art. 6.1.a) — per comunicazioni marketing opzionali e cookie (revocabile in qualsiasi momento).",
        ],
      },
      {
        h: "5. Terze parti",
        list: [
          "Resend (invio email) — https://resend.com/legal/privacy-policy",
          "Google LLC (YouTube Data API per lo streaming) — https://policies.google.com/privacy",
          "PostHog (product analytics) — https://posthog.com/privacy",
          "MongoDB Atlas (hosting database) — https://www.mongodb.com/legal/privacy-policy",
          "Aruba S.p.A. (hosting provider) — https://www.aruba.it/informativa-privacy.aspx",
        ],
      },
      {
        h: "6. Conservazione dei dati",
        list: [
          "Dati dell'account: conservati fino a richiesta di cancellazione.",
          "Log di visualizzazione e heartbeat: cancellati automaticamente dopo 36 mesi.",
          "Messaggi Q&A delle dirette: cancellati automaticamente dopo 36 mesi.",
          "Backup: cifrati, ruotati ogni 30 giorni.",
        ],
      },
      {
        h: "7. I tuoi diritti",
        p: "Ai sensi del GDPR hai diritto di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione al trattamento dei tuoi dati. Puoi inoltre proporre reclamo al Garante per la Protezione dei Dati Personali. Per esercitare qualsiasi diritto, scrivi a info@wallboardacademy.com — rispondiamo entro 30 giorni.",
      },
      {
        h: "8. Cookie",
        p: "Utilizziamo solo cookie strettamente necessari (sessione, autenticazione) e un cookie analytics impostato da PostHog. Non utilizziamo cookie pubblicitari o di tracciamento di terze parti.",
      },
      {
        h: "9. Minori",
        p: "La piattaforma è destinata a professionisti medici maggiorenni. Non raccogliamo consapevolmente dati da minori.",
      },
      {
        h: "10. Sicurezza",
        p: "Le password sono cifrate con bcrypt. Tutto il traffico è cifrato tramite HTTPS/TLS 1.3. L'accesso al database è ristretto tramite IP allow-list e richiede MFA.",
      },
      {
        h: "11. Modifiche a questa policy",
        p: "Questa Informativa può essere aggiornata. La data dell'ultima revisione è mostrata in cima. Le modifiche significative saranno comunicate via email.",
      },
      {
        h: "12. Contatto",
        p: "Wall Board Academy — info@wallboardacademy.com — www.wallboardacademy.com",
      },
    ],
    back: "← Torna alla home",
  },
};

export default function Privacy() {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.en;

  return (
    <div data-testid="privacy-page" className="min-h-screen px-6 pt-32 pb-24 md:px-10">
      <div className="mx-auto max-w-3xl">
        <Link
          to="/"
          data-testid="privacy-back"
          className="mb-8 inline-block font-mono text-[10px] uppercase tracking-[0.3em] text-cyan hover:text-white"
        >
          {c.back}
        </Link>

        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-head text-4xl font-black uppercase tracking-tight text-white md:text-6xl"
        >
          {c.title}
        </motion.h1>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
          {c.lastUpdated}
        </p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mt-10 font-body text-base leading-relaxed text-slate-300"
        >
          {c.intro}
        </motion.p>

        <div className="mt-12 space-y-10">
          {c.sections.map((s, i) => (
            <motion.section
              key={s.h}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: 0.03 * i }}
            >
              <h2 className="font-head text-xl font-bold uppercase tracking-widest text-cyan">
                {s.h}
              </h2>
              {s.p && (
                <p className="mt-3 font-body text-base leading-relaxed text-slate-300">
                  {s.p}
                </p>
              )}
              {s.list && (
                <ul className="mt-3 list-disc space-y-1.5 pl-6 font-body text-base leading-relaxed text-slate-300">
                  {s.list.map((li, j) => (
                    <li key={j}>{li}</li>
                  ))}
                </ul>
              )}
            </motion.section>
          ))}
        </div>

        <div className="mt-16 border-t border-white/10 pt-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
            © {new Date().getFullYear()} Wall Board Academy — All rights reserved
          </p>
        </div>
      </div>
    </div>
  );
}
